<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card style="width: 540px; max-width: 95vw" class="manage-ps-dialog">
      <q-toolbar class="bg-blue-brand text-white">
        <q-toolbar-title>Pipeline Manager</q-toolbar-title>
        <q-btn v-close-popup flat round dense icon="close" />
      </q-toolbar>

      <q-tabs
        v-model="tab"
        dense
        class="text-grey"
        active-color="blue-brand"
        indicator-color="blue-brand"
        align="left"
        update-csv-file
        narrow-indicator
      >
        <q-tab name="create" label="Create New" />
        <q-tab name="update" label="Update Existing" />
      </q-tabs>

      <q-separator />

      <q-tab-panels v-model="tab" animated keep-alive>
        <!-- Create New Pipeline Section -->
        <q-tab-panel name="create">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-input
              v-model="createName"
              dense
              outlined
              color="blue-brand"
              label="Name e.g., Oklahoma City to Tulsa"
              :error="createNameError !== ''"
              :error-message="createNameError"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="createDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (createDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :error="createDiameterError !== ''"
                :error-message="createDiameterError"
                :rules="[createDiameterRule]"
                reactive-rules
                class="col"
              />
              <q-select
                v-model="createDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                style="width: 90px"
              />
            </div>

            <div
              class="csv-drop-zone"
              :class="{ 'csv-drop-zone--active': createDragOver }"
              @dragover.prevent="createDragOver = true"
              @dragenter.prevent="createDragOver = true"
              @dragleave.prevent="createDragOver = false"
              @drop.prevent="onDropCsv($event, 'create')"
            >
              <q-file
                v-model="createCsvFile"
                dense
                outlined
                color="blue-brand"
                label="Survey Markers and Tracking Locations (CSV) File Upload"
                accept=".csv"
                clearable
                :error="createCsvFileError !== ''"
                :error-message="createCsvFileError"
              >
                <template #prepend>
                  <q-icon name="attach_file" />
                </template>
                <template v-if="createCsvFile && createCsvValidation" #append>
                  <q-badge
                    v-if="createCsvValidation.errors.length > 0"
                    color="negative"
                    :label="
                      createCsvValidation.errors.length +
                      ' error' +
                      (createCsvValidation.errors.length > 1 ? 's' : '')
                    "
                    class="q-mr-xs"
                  />
                  <q-badge
                    v-if="createCsvValidation.warnings.length > 0"
                    color="amber-8"
                    :label="
                      createCsvValidation.warnings.length +
                      ' warning' +
                      (createCsvValidation.warnings.length > 1 ? 's' : '')
                    "
                  />
                </template>
              </q-file>
              <div
                v-if="!createCsvFile"
                class="text-caption text-grey-6 text-center q-mt-xs"
              >
                or drag &amp; drop a CSV file here
              </div>
            </div>

            <!-- Format hint (shown when no file selected) -->
            <div v-if="!createCsvFile" class="text-caption text-grey-7 q-mt-xs">
              <q-icon name="info" size="14px" class="q-mr-xs" />
              Required columns: AGM Marker Name, Latitude, Longitude,
              <span class="cursor-pointer text-primary">
                State
                <q-tooltip
                  class="text-body2"
                  anchor="top middle"
                  self="bottom middle"
                  max-width="220px"
                >
                  Valid values: TRACK, SKIP
                </q-tooltip> </span
              >,
              <span class="cursor-pointer text-primary">
                Description
                <q-tooltip
                  class="text-body2"
                  anchor="top middle"
                  self="bottom middle"
                  max-width="280px"
                >
                  Valid values: AGM, ATTACHMENT, BEND, LAUNCH, MAGNET, OTHER,
                  PIPE, RECEIVE, TAP, TEE, VALVE, WELD
                </q-tooltip> </span
              >.
              <a
                href="#"
                class="text-primary"
                style="text-decoration: underline"
                @click.prevent="onDownloadExampleCsv"
                >Download example CSV</a
              >
              <div class="q-mt-xs row items-center no-wrap">
                <span class="text-grey-8">Header (first line):&nbsp;</span>
                <code class="text-grey-9" style="font-size: 11px">
                  AGM Marker Name,Latitude,Longitude,State,Description
                </code>
                <q-btn
                  flat
                  round
                  dense
                  size="xs"
                  icon="content_copy"
                  color="grey-7"
                  title="Copy header to clipboard"
                  class="q-ml-xs"
                  @click="onCopyHeader"
                />
              </div>
            </div>

            <!-- Validation feedback (shown after file is parsed) -->
            <div v-if="createCsvFile && createCsvValidation" class="q-mt-xs">
              <div v-if="createCsvValidation.errors.length > 0">
                <div
                  v-for="(err, idx) in showAllCreateErrors
                    ? createCsvValidation.errors
                    : createCsvValidation.errors.slice(0, 3)"
                  :key="'ce-' + idx"
                  class="text-caption text-negative row items-start no-wrap q-mb-xs"
                  :style="hasRowRef(err) ? { cursor: 'pointer' } : {}"
                  @click="scrollToErrorRow('createPreviewTable', err)"
                >
                  <q-icon
                    name="error"
                    size="16px"
                    class="q-mr-xs"
                    color="negative"
                    style="flex-shrink: 0; margin-top: 1px"
                  />
                  <span>{{ err }}</span>
                </div>
                <a
                  v-if="
                    createCsvValidation.errors.length > 3 &&
                    !showAllCreateErrors
                  "
                  href="#"
                  class="text-caption text-primary"
                  @click.prevent="showAllCreateErrors = true"
                >
                  Show {{ createCsvValidation.errors.length - 3 }} more error(s)
                </a>
                <a
                  v-if="
                    createCsvValidation.errors.length > 3 && showAllCreateErrors
                  "
                  href="#"
                  class="text-caption text-primary"
                  @click.prevent="showAllCreateErrors = false"
                >
                  Show fewer errors
                </a>
              </div>
              <div v-if="createCsvValidation.warnings.length > 0">
                <div
                  v-for="(warn, idx) in createCsvValidation.warnings"
                  :key="'cw-' + idx"
                  class="text-caption text-amber-9 row items-start no-wrap q-mb-xs"
                >
                  <q-icon
                    name="warning"
                    size="16px"
                    class="q-mr-xs"
                    color="amber-8"
                    style="flex-shrink: 0; margin-top: 1px"
                  />
                  <span>{{ warn }}</span>
                </div>
              </div>
              <div
                v-if="createCsvValidation.valid"
                class="text-caption text-positive row items-center no-wrap"
              >
                <q-icon
                  name="check_circle"
                  size="16px"
                  class="q-mr-xs"
                  color="positive"
                />
                <span
                  >{{ createCsvValidation.parsedRows.length }} data rows,
                  {{ createCsvValidation.markerCount }} markers parsed &mdash;
                  {{ createCsvValidation.firstMarker }} &rarr;
                  {{ createCsvValidation.lastMarker
                  }}<span v-if="createCsvValidation.skipCount > 0">
                    &middot; {{ createCsvValidation.skipCount }} SKIP</span
                  ></span
                >
              </div>
            </div>

            <!-- Parsed data table preview -->
            <q-table
              v-if="
                createCsvFile &&
                createCsvValidation &&
                createCsvValidation.parsedRows.length > 0
              "
              ref="createPreviewTable"
              :rows="createCsvValidation.parsedRows"
              :columns="csvPreviewColumns"
              row-key="row"
              flat
              bordered
              dense
              hide-bottom
              :rows-per-page-options="[0]"
              class="q-mt-sm csv-preview-table"
              :style="{ maxHeight: '200px', overflowY: 'auto' }"
            >
              <template #body="props">
                <q-tr :props="props" :data-row="props.row.row">
                  <q-td
                    v-for="col in props.cols"
                    :key="col.name"
                    :props="props"
                    :class="{
                      'bg-red-1': props.row.errorFields.includes(col.name),
                    }"
                  >
                    {{ col.value }}
                  </q-td>
                </q-tr>
              </template>
            </q-table>

            <div>
              <q-btn
                class="btn-brand"
                label="Create"
                :loading="loading"
                :disable="
                  !!(
                    createCsvFile &&
                    (!createCsvValidation || !createCsvValidation.valid)
                  )
                "
                @click="onCreateSubmit"
              />
            </div>

            <!-- Mini map preview for create -->
            <div v-show="createCsvFile" class="q-mt-md">
              <div class="text-caption text-grey-8 q-mb-xs">
                Pipeline Preview
              </div>
              <div
                ref="createMiniMap"
                style="
                  height: 250px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
            </div>
          </div>
        </q-tab-panel>

        <!-- Update Existing Pipeline Section -->
        <q-tab-panel name="update">
          <div class="q-gutter-md" style="max-width: 500px">
            <q-select
              v-model="selectedPipelineSection"
              dense
              outlined
              color="blue-brand"
              label="Name"
              :options="pipelineSectionOptions"
              :loading="loading"
              clearable
              @update:model-value="onPipelineSectionSelected"
              @clear="onPipelineSectionCleared"
            />

            <div class="row items-start q-gutter-sm">
              <q-input
                v-model="updateDiameter"
                dense
                outlined
                color="blue-brand"
                :label="
                  'Diameter (' +
                  (updateDiameterUnit === 'in' ? 'inches' : 'cm') +
                  ')'
                "
                :disable="!selectedPipelineSection"
                :rules="[(val) => !!val || 'Diameter is required.']"
                class="col"
              />
              <q-select
                v-model="updateDiameterUnit"
                dense
                outlined
                color="blue-brand"
                :options="[
                  { label: 'in', value: 'in' },
                  { label: 'cm', value: 'cm' },
                ]"
                emit-value
                map-options
                :disable="!selectedPipelineSection"
                style="width: 90px"
              />
            </div>

            <!-- Current tracking file -->
            <div v-if="selectedPipelineSection && currentTrackingFileName">
              <div class="text-caption text-grey-8 q-mb-xs">
                Current Tracking File
              </div>
              <div class="row items-center q-gutter-sm">
                <span class="text-body2">{{ currentTrackingFileName }}</span>
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="visibility"
                  color="blue-brand"
                  title="View file"
                  @click="onViewTrackingFile"
                />
                <q-btn
                  flat
                  dense
                  size="sm"
                  icon="download"
                  color="blue-brand"
                  title="Download file"
                  @click="onDownloadTrackingFile"
                />
              </div>
            </div>

            <div
              class="csv-drop-zone"
              :class="{
                'csv-drop-zone--active':
                  updateDragOver && selectedPipelineSection,
              }"
              @dragover.prevent="updateDragOver = true"
              @dragenter.prevent="updateDragOver = true"
              @dragleave.prevent="updateDragOver = false"
              @drop.prevent="onDropCsv($event, 'update')"
            >
              <q-file
                v-model="updateCsvFile"
                dense
                outlined
                color="blue-brand"
                label="Survey Markers and Tracking Locations (CSV) File Upload"
                accept=".csv"
                clearable
                :disable="!selectedPipelineSection"
              >
                <template #prepend>
                  <q-icon name="attach_file" />
                </template>
                <template v-if="updateCsvFile && updateCsvValidation" #append>
                  <q-badge
                    v-if="updateCsvValidation.errors.length > 0"
                    color="negative"
                    :label="
                      updateCsvValidation.errors.length +
                      ' error' +
                      (updateCsvValidation.errors.length > 1 ? 's' : '')
                    "
                    class="q-mr-xs"
                  />
                  <q-badge
                    v-if="updateCsvValidation.warnings.length > 0"
                    color="amber-8"
                    :label="
                      updateCsvValidation.warnings.length +
                      ' warning' +
                      (updateCsvValidation.warnings.length > 1 ? 's' : '')
                    "
                  />
                </template>
              </q-file>
              <div
                v-if="!updateCsvFile && selectedPipelineSection"
                class="text-caption text-grey-6 text-center q-mt-xs"
              >
                or drag &amp; drop a CSV file here
              </div>
            </div>

            <!-- Format hint (shown when no replacement file selected) -->
            <div
              v-if="!updateCsvFile && selectedPipelineSection"
              class="text-caption text-grey-7 q-mt-xs"
            >
              <q-icon name="info" size="14px" class="q-mr-xs" />
              Required columns: AGM Marker Name, Latitude, Longitude,
              <span class="cursor-pointer text-primary">
                State
                <q-tooltip
                  class="text-body2"
                  anchor="top middle"
                  self="bottom middle"
                  max-width="220px"
                >
                  Valid values: TRACK, SKIP
                </q-tooltip> </span
              >,
              <span class="cursor-pointer text-primary">
                Description
                <q-tooltip
                  class="text-body2"
                  anchor="top middle"
                  self="bottom middle"
                  max-width="280px"
                >
                  Valid values: AGM, ATTACHMENT, BEND, LAUNCH, MAGNET, OTHER,
                  PIPE, RECEIVE, TAP, TEE, VALVE, WELD
                </q-tooltip> </span
              >.
              <a
                href="#"
                class="text-primary"
                style="text-decoration: underline"
                @click.prevent="onDownloadExampleCsv"
                >Download example CSV</a
              >
              <div class="q-mt-xs row items-center no-wrap">
                <span class="text-grey-8">Header (first line):&nbsp;</span>
                <code class="text-grey-9" style="font-size: 11px">
                  AGM Marker Name,Latitude,Longitude,State,Description
                </code>
                <q-btn
                  flat
                  round
                  dense
                  size="xs"
                  icon="content_copy"
                  color="grey-7"
                  title="Copy header to clipboard"
                  class="q-ml-xs"
                  @click="onCopyHeader"
                />
              </div>
            </div>

            <!-- Validation feedback for update CSV -->
            <div v-if="updateCsvFile && updateCsvValidation" class="q-mt-xs">
              <div v-if="updateCsvValidation.errors.length > 0">
                <div
                  v-for="(err, idx) in showAllUpdateErrors
                    ? updateCsvValidation.errors
                    : updateCsvValidation.errors.slice(0, 3)"
                  :key="'ue-' + idx"
                  class="text-caption text-negative row items-start no-wrap q-mb-xs"
                  :style="hasRowRef(err) ? { cursor: 'pointer' } : {}"
                  @click="scrollToErrorRow('updatePreviewTable', err)"
                >
                  <q-icon
                    name="error"
                    size="16px"
                    class="q-mr-xs"
                    color="negative"
                    style="flex-shrink: 0; margin-top: 1px"
                  />
                  <span>{{ err }}</span>
                </div>
                <a
                  v-if="
                    updateCsvValidation.errors.length > 3 &&
                    !showAllUpdateErrors
                  "
                  href="#"
                  class="text-caption text-primary"
                  @click.prevent="showAllUpdateErrors = true"
                >
                  Show {{ updateCsvValidation.errors.length - 3 }} more error(s)
                </a>
                <a
                  v-if="
                    updateCsvValidation.errors.length > 3 && showAllUpdateErrors
                  "
                  href="#"
                  class="text-caption text-primary"
                  @click.prevent="showAllUpdateErrors = false"
                >
                  Show fewer errors
                </a>
              </div>
              <div v-if="updateCsvValidation.warnings.length > 0">
                <div
                  v-for="(warn, idx) in updateCsvValidation.warnings"
                  :key="'uw-' + idx"
                  class="text-caption text-amber-9 row items-start no-wrap q-mb-xs"
                >
                  <q-icon
                    name="warning"
                    size="16px"
                    class="q-mr-xs"
                    color="amber-8"
                    style="flex-shrink: 0; margin-top: 1px"
                  />
                  <span>{{ warn }}</span>
                </div>
              </div>
              <div
                v-if="updateCsvValidation.valid"
                class="text-caption text-positive row items-center no-wrap"
              >
                <q-icon
                  name="check_circle"
                  size="16px"
                  class="q-mr-xs"
                  color="positive"
                />
                <span
                  >{{ updateCsvValidation.parsedRows.length }} data rows,
                  {{ updateCsvValidation.markerCount }} markers parsed &mdash;
                  {{ updateCsvValidation.firstMarker }} &rarr;
                  {{ updateCsvValidation.lastMarker
                  }}<span v-if="updateCsvValidation.skipCount > 0">
                    &middot; {{ updateCsvValidation.skipCount }} SKIP</span
                  ></span
                >
              </div>
            </div>

            <!-- Parsed data table preview -->
            <q-table
              v-if="
                updateCsvFile &&
                updateCsvValidation &&
                updateCsvValidation.parsedRows.length > 0
              "
              ref="updatePreviewTable"
              :rows="updateCsvValidation.parsedRows"
              :columns="csvPreviewColumns"
              row-key="row"
              flat
              bordered
              dense
              hide-bottom
              :rows-per-page-options="[0]"
              class="q-mt-sm csv-preview-table"
              :style="{ maxHeight: '200px', overflowY: 'auto' }"
            >
              <template #body="props">
                <q-tr :props="props" :data-row="props.row.row">
                  <q-td
                    v-for="col in props.cols"
                    :key="col.name"
                    :props="props"
                    :class="{
                      'bg-red-1': props.row.errorFields.includes(col.name),
                    }"
                  >
                    {{ col.value }}
                  </q-td>
                </q-tr>
              </template>
            </q-table>

            <div class="row q-gutter-sm">
              <q-btn
                class="btn-brand"
                label="Update"
                :disable="
                  !selectedPipelineSection ||
                  !!(
                    updateCsvFile &&
                    (!updateCsvValidation || !updateCsvValidation.valid)
                  )
                "
                :loading="loading"
                @click="onUpdateSubmit"
              />
              <q-btn
                color="negative"
                label="Delete"
                :disable="!selectedPipelineSection"
                :loading="loading"
                @click="onDeleteSubmit"
              />
            </div>

            <!-- Mini map preview -->
            <div
              v-show="selectedPipelineSection && currentTrackingFile"
              class="q-mt-md"
            >
              <div class="text-caption text-grey-8 q-mb-xs">
                Pipeline Preview
              </div>
              <div
                ref="miniMap"
                style="
                  height: 250px;
                  width: 100%;
                  border: 1px solid #ccc;
                  border-radius: 4px;
                "
              />
            </div>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>

    <!-- View Tracking File Dialog -->
    <q-dialog
      v-model="viewTrackingFileDialog"
      maximized
      @hide="onViewTrackingFileDialogHide"
    >
      <q-card>
        <q-toolbar class="bg-blue-brand text-white">
          <q-toolbar-title>{{ currentTrackingFileName }}</q-toolbar-title>
          <q-btn
            flat
            round
            dense
            icon="download"
            title="Download"
            @click="onDownloadTrackingFile"
          />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-toolbar>
        <q-card-section
          class="q-pa-md"
          style="overflow: auto; max-height: calc(100vh - 50px)"
        >
          <pre
            style="
              white-space: pre-wrap;
              word-break: break-all;
              font-size: 13px;
            "
            >{{ currentTrackingFile }}</pre
          >
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-dialog>

  <InstructionCard
    v-model="showInstructions"
    title="Pipelines"
    icon="description"
    message="Create a pipeline by entering a name, diameter, and uploading a CSV file with your marker positions."
    tip="Download an example CSV to see the expected format."
    tip-action-label="Download example CSV"
    :z-index="7000"
    @tip-action="onDownloadExampleCsv"
  />
</template>

<script lang="ts">
import L from 'leaflet';
import { defineComponent, markRaw } from 'vue';
import PipelineSectionService from '@/services/PipelineSectionService';
import InstructionCard from '@/components/InstructionCard.vue';
import { CreatePipelineRequest } from '@/models/PipelineSection/CreatePipelineRequest';
import { UpdatePipelineRequest } from '@/models/PipelineSection/UpdatePipelineRequest';
import { PipelineSection } from '@/models/PipelineSection/PipelineSection';

export default defineComponent({
  name: 'PipelineSectionManager',
  components: { InstructionCard },
  props: {
    modelValue: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'sections-updated'],
  data() {
    return {
      tab: 'create',
      loading: false,

      // Create form state
      createName: '',
      createNameError: '',
      createDiameter: '',
      createDiameterError: '',
      createDiameterUnit: 'in' as 'in' | 'cm',
      createCsvFile: null as File | null,
      createCsvFileError: '',
      showAllCreateErrors: false,
      createDragOver: false,

      // Update form state
      selectedPipelineSection: null as any,
      updateDiameter: '',
      updateDiameterUnit: 'in' as 'in' | 'cm',
      updateCsvFile: null as File | null,
      showAllUpdateErrors: false,
      updateDragOver: false,
      currentTrackingFileName: '',
      currentTrackingFile: '',
      viewTrackingFileDialog: false,

      // Full pipeline section data from API
      pipelineSections: [] as PipelineSection[],

      // Map preview markers for CSV points (main map, used by view dialog)
      csvMapMarkers: [] as L.Marker[],
      csvPolyline: null as L.Polyline | null,

      // Mini map for update tab
      miniMap: null as L.Map | null,
      miniMapLayer: null as L.LayerGroup | null,

      // Mini map for create tab
      createMiniMap: null as L.Map | null,
      createMiniMapLayer: null as L.LayerGroup | null,

      // Dropdown options populated from API
      pipelineSectionOptions: [] as any[],
      showInstructions: false,

      // CSV validation state
      createCsvValidation: null as {
        valid: boolean;
        errors: string[];
        warnings: string[];
        markerCount: number;
        firstMarker: string;
        lastMarker: string;
        skipCount: number;
        parsedRows: {
          row: number;
          name: string;
          lat: string;
          lon: string;
          state: string;
          description: string;
          hasError: boolean;
          errorFields: string[];
        }[];
      } | null,
      updateCsvValidation: null as {
        valid: boolean;
        errors: string[];
        warnings: string[];
        markerCount: number;
        firstMarker: string;
        lastMarker: string;
        skipCount: number;
        parsedRows: {
          row: number;
          name: string;
          lat: string;
          lon: string;
          state: string;
          description: string;
          hasError: boolean;
          errorFields: string[];
        }[];
      } | null,
    };
  },
  computed: {
    map(): any {
      return this.$store.state.map;
    },
    csvPreviewColumns(): any[] {
      return [
        { name: 'row', label: 'Row', field: 'row', align: 'left' },
        {
          name: 'name',
          label: 'AGM Marker Name',
          field: 'name',
          align: 'left',
        },
        { name: 'lat', label: 'Latitude', field: 'lat', align: 'left' },
        { name: 'lon', label: 'Longitude', field: 'lon', align: 'left' },
        { name: 'state', label: 'State', field: 'state', align: 'left' },
        {
          name: 'description',
          label: 'Description',
          field: 'description',
          align: 'left',
        },
      ];
    },
  },
  watch: {
    modelValue(val: boolean) {
      if (val) {
        this.showInstructions = true;
        this.resetForm();
        this.loadPipelineSections();
      } else {
        this.showInstructions = false;
        this.destroyMiniMap();
        this.destroyCreateMiniMap();
      }
    },
    createCsvFile(file: File | null) {
      if (!file) {
        this.createCsvValidation = null;
        this.showAllCreateErrors = false;
        this.clearCreateMiniMap();
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.createCsvValidation = this.validateCsv(content);
          this.plotCsvPointsOnCreateMiniMap(content);
        }
      };
      reader.readAsText(file);
    },
    updateDiameterUnit(newVal: 'in' | 'cm', oldVal: 'in' | 'cm') {
      const val = parseFloat(this.updateDiameter);
      if (!isNaN(val) && val > 0) {
        const meters = oldVal === 'in' ? val * 0.0254 : val * 0.01;
        const converted = newVal === 'in' ? meters / 0.0254 : meters / 0.01;
        this.updateDiameter = String(parseFloat(converted.toFixed(4)));
      }
    },
    updateCsvFile(file: File | null) {
      if (!file) {
        this.updateCsvValidation = null;
        this.showAllUpdateErrors = false;
        if (this.currentTrackingFile) {
          this.$nextTick(() => {
            this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
          });
        } else {
          this.clearMiniMap();
        }
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        if (content) {
          this.updateCsvValidation = this.validateCsv(content);
          this.plotCsvPointsOnMiniMap(content);
        }
      };
      reader.readAsText(file);
    },
  },
  methods: {
    onDownloadExampleCsv() {
      const link = document.createElement('a');
      link.href = `${process.env.BASE_URL || '/'}example.csv`;
      link.download = 'example.csv';
      link.click();
    },

    onCopyHeader() {
      navigator.clipboard
        .writeText('AGM Marker Name,Latitude,Longitude,State,Description')
        .then(() => {
          this.$q.notify({
            color: 'positive',
            message: 'Header copied to clipboard.',
            timeout: 1500,
          });
        });
    },

    onDropCsv(event: DragEvent, target: 'create' | 'update') {
      this.createDragOver = false;
      this.updateDragOver = false;
      if (target === 'update' && !this.selectedPipelineSection) return;
      const file = event.dataTransfer?.files?.[0];
      if (!file) return;
      if (!file.name.toLowerCase().endsWith('.csv')) {
        this.$q.notify({
          color: 'warning',
          message: 'Only .csv files are accepted.',
          timeout: 2000,
        });
        return;
      }
      if (target === 'create') {
        this.createCsvFile = file;
      } else {
        this.updateCsvFile = file;
      }
    },

    hasRowRef(errMsg: string): boolean {
      return /rows?\s+\d+/i.test(errMsg);
    },

    scrollToErrorRow(tableRef: string, errMsg: string) {
      const match = errMsg.match(/rows?\s+(\d+)/i);
      if (!match) return;
      const rowNum = match[1];
      const table = (this.$refs[tableRef] as any)?.$el;
      if (!table) return;
      const tr = table.querySelector(`tr[data-row="${rowNum}"]`);
      if (tr) {
        tr.scrollIntoView({ block: 'center', behavior: 'smooth' });
        tr.classList.add('highlight-flash');
        setTimeout(() => tr.classList.remove('highlight-flash'), 1500);
      }
    },

    validateCsv(content: string): {
      valid: boolean;
      errors: string[];
      warnings: string[];
      markerCount: number;
      firstMarker: string;
      lastMarker: string;
      skipCount: number;
      parsedRows: {
        row: number;
        name: string;
        lat: string;
        lon: string;
        state: string;
        description: string;
        hasError: boolean;
        errorFields: string[];
      }[];
    } {
      const errors: string[] = [];
      const warnings: string[] = [];
      let markerCount = 0;
      let firstMarker = '';
      let lastMarker = '';
      let skipCount = 0;
      const parsedRows: {
        row: number;
        name: string;
        lat: string;
        lon: string;
        state: string;
        description: string;
        hasError: boolean;
        errorFields: string[];
      }[] = [];

      // Strip UTF-8 BOM if present
      if (content.charCodeAt(0) === 0xfeff) {
        content = content.slice(1);
        warnings.push(
          'File contains a UTF-8 BOM (byte order mark) which was automatically removed.'
        );
      }

      const lines = content
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l.length > 0);

      if (lines.length === 0) {
        errors.push('File is empty.');
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          parsedRows,
        };
      }

      const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      const stateIdx = header.indexOf('state');
      const descIdx = header.indexOf('description');

      if (latIdx === -1) errors.push('Missing required column: Latitude');
      if (lonIdx === -1) errors.push('Missing required column: Longitude');
      if (nameIdx === -1)
        errors.push('Missing required column: AGM Marker Name');
      if (stateIdx === -1) errors.push('Missing required column: State');
      if (descIdx === -1) errors.push('Missing required column: Description');

      if (
        latIdx === -1 ||
        lonIdx === -1 ||
        nameIdx === -1 ||
        stateIdx === -1 ||
        descIdx === -1
      ) {
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          parsedRows,
        };
      }

      if (lines.length < 3) {
        errors.push('At least 2 marker rows are required.');
        return {
          valid: false,
          errors,
          warnings,
          markerCount,
          firstMarker,
          lastMarker,
          skipCount,
          parsedRows,
        };
      }

      const nonNumericCoordRows: { row: number; lat: string; lon: string }[] =
        [];
      const badLatRows: { row: number; value: string }[] = [];
      const badLonRows: { row: number; value: string }[] = [];
      const badColCountRows: number[] = [];
      const badStateRows: { row: number; value: string }[] = [];
      const badDescRows: { row: number; value: string }[] = [];
      const emptyNameRows: number[] = [];
      const markers: string[] = [];
      const descriptions: { row: number; value: string }[] = [];
      const nameOccurrences: Record<string, number[]> = {};
      const coordOccurrences: Record<string, number[]> = {};
      const validDescriptions = new Set([
        'AGM',
        'ATTACHMENT',
        'BEND',
        'LAUNCH',
        'MAGNET',
        'OTHER',
        'PIPE',
        'RECEIVE',
        'TAP',
        'TEE',
        'VALVE',
        'WELD',
      ]);

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c) => c.trim());

        if (cols.length !== 5) {
          badColCountRows.push(i + 1);
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx] || '',
            lon: cols[lonIdx] || '',
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: ['name', 'lat', 'lon', 'state', 'description'],
          });
          continue;
        }

        const lat = Number(cols[latIdx]);
        const lon = Number(cols[lonIdx]);

        if (isNaN(lat) || isNaN(lon)) {
          nonNumericCoordRows.push({
            row: i + 1,
            lat: cols[latIdx],
            lon: cols[lonIdx],
          });
          const coordErrors: string[] = [];
          if (isNaN(lat)) coordErrors.push('lat');
          if (isNaN(lon)) coordErrors.push('lon');
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: coordErrors,
          });
          continue;
        }
        if (lat < -90 || lat > 90) {
          badLatRows.push({ row: i + 1, value: cols[latIdx] });
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: ['lat'],
          });
          continue;
        }
        if (lon < -180 || lon > 180) {
          badLonRows.push({ row: i + 1, value: cols[lonIdx] });
          parsedRows.push({
            row: i + 1,
            name: cols[nameIdx] || '',
            lat: cols[latIdx],
            lon: cols[lonIdx],
            state: cols[stateIdx] || '',
            description: cols[descIdx] || '',
            hasError: true,
            errorFields: ['lon'],
          });
          continue;
        }

        markerCount++;
        const rawName = nameIdx !== -1 ? cols[nameIdx] || '' : '';
        if (!rawName) {
          emptyNameRows.push(i + 1);
        }
        const name = rawName || `Row ${i + 1}`;
        markers.push(name);

        // Track name occurrences for duplicate check
        if (!nameOccurrences[name]) {
          nameOccurrences[name] = [];
        }
        nameOccurrences[name].push(i + 1);

        // Track coordinate occurrences for duplicate check
        const coordKey = `${lat},${lon}`;
        if (!coordOccurrences[coordKey]) {
          coordOccurrences[coordKey] = [];
        }
        coordOccurrences[coordKey].push(i + 1);

        // Track description
        const desc = descIdx !== -1 ? (cols[descIdx] || '').toUpperCase() : '';
        descriptions.push({ row: i + 1, value: desc });
        if (desc && !validDescriptions.has(desc)) {
          badDescRows.push({ row: i + 1, value: cols[descIdx] });
        }

        if (stateIdx !== -1) {
          const state = (cols[stateIdx] || '').toUpperCase();
          if (state === 'SKIP') skipCount++;
          if (state !== 'TRACK' && state !== 'SKIP') {
            badStateRows.push({ row: i + 1, value: cols[stateIdx] });
          }
        }

        const errorFields: string[] = [];
        if (!rawName) errorFields.push('name');
        if (desc !== '' && !validDescriptions.has(desc))
          errorFields.push('description');
        if (
          stateIdx !== -1 &&
          (cols[stateIdx] || '').toUpperCase() !== 'TRACK' &&
          (cols[stateIdx] || '').toUpperCase() !== 'SKIP'
        )
          errorFields.push('state');
        const rowHasError = errorFields.length > 0;
        parsedRows.push({
          row: i + 1,
          name: rawName || '',
          lat: cols[latIdx],
          lon: cols[lonIdx],
          state: cols[stateIdx] || '',
          description: cols[descIdx] || '',
          hasError: rowHasError,
          errorFields,
        });
      }

      if (badColCountRows.length > 0) {
        const shown = badColCountRows.slice(0, 5);
        const msg = `Row${badColCountRows.length > 1 ? 's' : ''} ${shown.join(
          ', '
        )} must have exactly 5 columns${
          badColCountRows.length > 5
            ? ` and ${badColCountRows.length - 5} more`
            : ''
        }`;
        errors.push(msg);
      }

      if (nonNumericCoordRows.length > 0) {
        const shown = nonNumericCoordRows.slice(0, 5);
        const msgs = shown.map(
          (r) => `row ${r.row} (Latitude: "${r.lat}", Longitude: "${r.lon}")`
        );
        const extra =
          nonNumericCoordRows.length > 5
            ? ` and ${nonNumericCoordRows.length - 5} more`
            : '';
        errors.push(`Non-numeric coordinates on ${msgs.join('; ')}${extra}`);
      }

      if (badLatRows.length > 0) {
        const shown = badLatRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badLatRows.length > 5 ? ` and ${badLatRows.length - 5} more` : '';
        errors.push(
          `Invalid latitude: ${msgs.join(
            '; '
          )}${extra} (must be between -90 and 90)`
        );
      }

      if (badLonRows.length > 0) {
        const shown = badLonRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badLonRows.length > 5 ? ` and ${badLonRows.length - 5} more` : '';
        errors.push(
          `Invalid longitude: ${msgs.join(
            '; '
          )}${extra} (must be between -180 and 180)`
        );
      }

      if (badStateRows.length > 0) {
        const shown = badStateRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badStateRows.length > 5 ? ` and ${badStateRows.length - 5} more` : '';
        errors.push(
          `Invalid State value: ${msgs.join(
            '; '
          )}${extra} (must be "TRACK" or "SKIP")`
        );
      }

      if (emptyNameRows.length > 0) {
        const shown = emptyNameRows.slice(0, 5);
        const msg = `AGM Marker Name is empty on row${
          emptyNameRows.length > 1 ? 's' : ''
        } ${shown.join(', ')}${
          emptyNameRows.length > 5
            ? ` and ${emptyNameRows.length - 5} more`
            : ''
        }`;
        errors.push(msg);
      }

      if (markerCount < 2) {
        errors.push(
          `Only ${markerCount} valid marker(s) found; at least 2 are required.`
        );
      }

      // Check for duplicate AGM Marker Names
      const duplicates = Object.entries(nameOccurrences).filter(
        ([, rows]) => rows.length > 1
      );
      if (duplicates.length > 0) {
        const shown = duplicates.slice(0, 3);
        const msgs = shown.map(
          ([name, rows]) => `"${name}" (rows ${rows.join(', ')})`
        );
        const extra =
          duplicates.length > 3 ? ` and ${duplicates.length - 3} more` : '';
        errors.push(`Duplicate AGM Marker Names: ${msgs.join('; ')}${extra}`);
      }

      // Check for duplicate coordinates
      const dupCoords = Object.entries(coordOccurrences).filter(
        ([, rows]) => rows.length > 1
      );
      if (dupCoords.length > 0) {
        const shown = dupCoords.slice(0, 3);
        const msgs = shown.map(
          ([coord, rows]) => `(${coord}) on rows ${rows.join(', ')}`
        );
        const extra =
          dupCoords.length > 3 ? ` and ${dupCoords.length - 3} more` : '';
        errors.push(`Duplicate coordinates: ${msgs.join('; ')}${extra}`);
      }

      // Back-patch errorFields for duplicate rows
      if (duplicates.length > 0) {
        const dupNameRows = new Set(duplicates.flatMap(([, rows]) => rows));
        for (const pr of parsedRows) {
          if (dupNameRows.has(pr.row) && !pr.errorFields.includes('name')) {
            pr.errorFields.push('name');
            pr.hasError = true;
          }
        }
      }
      if (dupCoords.length > 0) {
        const dupCoordRows = new Set(dupCoords.flatMap(([, rows]) => rows));
        for (const pr of parsedRows) {
          if (dupCoordRows.has(pr.row)) {
            if (!pr.errorFields.includes('lat')) pr.errorFields.push('lat');
            if (!pr.errorFields.includes('lon')) pr.errorFields.push('lon');
            pr.hasError = true;
          }
        }
      }

      // Check for invalid Description values
      if (badDescRows.length > 0) {
        const shown = badDescRows.slice(0, 5);
        const msgs = shown.map((r) => `"${r.value}" on row ${r.row}`);
        const extra =
          badDescRows.length > 5 ? ` and ${badDescRows.length - 5} more` : '';
        errors.push(`Invalid Description: ${msgs.join('; ')}${extra}`);
      }

      // Check first row description is LAUNCH, last is RECEIVE
      if (descriptions.length > 0) {
        if (descriptions[0].value !== 'LAUNCH') {
          errors.push('First data row Description must be "LAUNCH".');
        }
        if (descriptions[descriptions.length - 1].value !== 'RECEIVE') {
          errors.push('Last data row Description must be "RECEIVE".');
        }
        const launchCount = descriptions.filter(
          (d) => d.value === 'LAUNCH'
        ).length;
        const receiveCount = descriptions.filter(
          (d) => d.value === 'RECEIVE'
        ).length;
        if (launchCount > 1) {
          errors.push(
            `"LAUNCH" must appear exactly once but was found ${launchCount} times.`
          );
        }
        if (receiveCount > 1) {
          errors.push(
            `"RECEIVE" must appear exactly once but was found ${receiveCount} times.`
          );
        }

        // Check LAUNCH/RECEIVE do not appear in middle rows
        const middleDescs = descriptions.slice(1, -1);
        const middleLaunch = middleDescs
          .filter((d) => d.value === 'LAUNCH')
          .map((d) => d.row);
        const middleReceive = middleDescs
          .filter((d) => d.value === 'RECEIVE')
          .map((d) => d.row);
        if (middleLaunch.length > 0) {
          errors.push(
            `"LAUNCH" must only appear in the first row but was also found on row${
              middleLaunch.length > 1 ? 's' : ''
            } ${middleLaunch.join(', ')}.`
          );
        }
        if (middleReceive.length > 0) {
          errors.push(
            `"RECEIVE" must only appear in the last row but was also found on row${
              middleReceive.length > 1 ? 's' : ''
            } ${middleReceive.join(', ')}.`
          );
        }
      }

      if (markers.length > 0) {
        firstMarker = markers[0];
        lastMarker = markers[markers.length - 1];
      }

      return {
        valid: errors.length === 0,
        errors,
        warnings,
        markerCount,
        firstMarker,
        lastMarker,
        skipCount,
        parsedRows,
      };
    },

    createDiameterRule(val: string): boolean | string {
      if (!val || val.trim().length === 0) return true;
      const num = parseFloat(val);
      if (isNaN(num) || num <= 0) return 'Diameter must be a positive number.';
      const meters =
        this.createDiameterUnit === 'in' ? num * 0.0254 : num * 0.01;
      if (meters < 0.1016 || meters > 1.27) {
        return this.createDiameterUnit === 'in'
          ? 'Diameter must be between 4 and 50 inches.'
          : 'Diameter must be between 10.16 and 127 cm.';
      }
      return true;
    },

    resetForm() {
      this.tab = 'create';
      this.createName = '';
      this.createNameError = '';
      this.createDiameter = '';
      this.createDiameterError = '';
      this.createDiameterUnit = 'in';
      this.createCsvFile = null;
      this.createCsvFileError = '';
      this.createCsvValidation = null;
      this.selectedPipelineSection = null;
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.updateCsvFile = null;
      this.updateCsvValidation = null;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.viewTrackingFileDialog = false;
    },

    loadPipelineSections() {
      this.loading = true;

      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          this.pipelineSectionOptions = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
        })
        .catch((err: any) => {
          console.error('Failed to load pipeline sections:', err);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onPipelineSectionSelected(val: any) {
      if (!val) {
        return;
      }
      const section = this.pipelineSections.find(
        (ps) => ps.pipelineSectionId === val.value
      );
      if (section) {
        if (section.diameter != null) {
          const converted =
            this.updateDiameterUnit === 'in'
              ? section.diameter / 0.0254
              : section.diameter / 0.01;
          this.updateDiameter = String(parseFloat(converted.toFixed(4)));
        } else {
          this.updateDiameter = '';
        }
        this.currentTrackingFileName = section.trackingFileName || '';
        this.currentTrackingFile = section.trackingFile || '';
        this.$nextTick(() => {
          this.plotCsvPointsOnMiniMap(this.currentTrackingFile);
        });
      }
    },

    onPipelineSectionCleared() {
      this.updateDiameter = '';
      this.updateDiameterUnit = 'in';
      this.updateCsvFile = null;
      this.currentTrackingFileName = '';
      this.currentTrackingFile = '';
      this.clearMiniMap();
    },

    onViewTrackingFile() {
      if (this.currentTrackingFile) {
        this.viewTrackingFileDialog = true;
        this.plotCsvPointsOnMap(this.currentTrackingFile);
      }
    },

    onViewTrackingFileDialogHide() {
      this.clearCsvMapMarkers();
    },

    plotCsvPointsOnMap(csv: string) {
      this.clearCsvMapMarkers();
      if (!this.map || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        const marker = L.marker(latlng).bindTooltip(name).addTo(this.map);
        this.csvMapMarkers.push(markRaw(marker));
      }

      if (latLngs.length > 1) {
        this.csvPolyline = markRaw(
          L.polyline(latLngs, {
            color: '#1976D2',
            weight: 2,
            dashArray: '6 4',
          }).addTo(this.map)
        );
        this.map.fitBounds(this.csvPolyline.getBounds());
      } else if (latLngs.length === 1) {
        this.map.setView(latLngs[0], 14);
      }
    },

    clearCsvMapMarkers() {
      for (const marker of this.csvMapMarkers) {
        marker.removeFrom(this.map);
      }
      this.csvMapMarkers = [];
      if (this.csvPolyline) {
        this.csvPolyline.removeFrom(this.map);
        this.csvPolyline = null;
      }
    },

    initMiniMap() {
      if (this.miniMap) return;
      const el = this.$refs.miniMap as HTMLElement;
      if (!el) return;

      this.miniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.miniMap as L.Map);

      this.miniMapLayer = markRaw(L.layerGroup().addTo(this.miniMap as L.Map));
    },

    clearMiniMap() {
      if (this.miniMapLayer) {
        this.miniMapLayer.clearLayers();
      }
    },

    destroyMiniMap() {
      if (this.miniMap) {
        this.miniMap.remove();
        this.miniMap = null;
        this.miniMapLayer = null;
      }
    },

    initCreateMiniMap() {
      if (this.createMiniMap) return;
      const el = this.$refs.createMiniMap as HTMLElement;
      if (!el) return;

      this.createMiniMap = markRaw(
        L.map(el, {
          zoomControl: true,
          attributionControl: false,
        }).setView([0, 0], 2)
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(this.createMiniMap as L.Map);

      this.createMiniMapLayer = markRaw(
        L.layerGroup().addTo(this.createMiniMap as L.Map)
      );
    },

    clearCreateMiniMap() {
      if (this.createMiniMapLayer) {
        this.createMiniMapLayer.clearLayers();
      }
    },

    destroyCreateMiniMap() {
      if (this.createMiniMap) {
        this.createMiniMap.remove();
        this.createMiniMap = null;
        this.createMiniMapLayer = null;
      }
    },

    plotCsvPointsOnCreateMiniMap(csv: string) {
      this.initCreateMiniMap();
      this.clearCreateMiniMap();
      if (!this.createMiniMap || !this.createMiniMapLayer || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        L.marker(latlng)
          .bindTooltip(name)
          .addTo(this.createMiniMapLayer as L.LayerGroup);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.createMiniMapLayer as L.LayerGroup);
      }

      setTimeout(() => {
        if (!this.createMiniMap) return;
        this.createMiniMap.invalidateSize();
        if (latLngs.length > 1) {
          this.createMiniMap.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          this.createMiniMap.setView(latLngs[0], 14);
        }
      }, 100);
    },

    plotCsvPointsOnMiniMap(csv: string) {
      this.initMiniMap();
      this.clearMiniMap();
      if (!this.miniMap || !this.miniMapLayer || !csv) return;

      const lines = csv
        .split('\n')
        .map((l: string) => l.trim())
        .filter((l: string) => l.length > 0);
      if (lines.length < 2) return;

      const header = lines[0]
        .split(',')
        .map((h: string) => h.trim().toLowerCase());
      const latIdx = header.indexOf('latitude');
      const lonIdx = header.indexOf('longitude');
      const nameIdx = header.indexOf('agm marker name');
      if (latIdx === -1 || lonIdx === -1) return;

      const latLngs: L.LatLng[] = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(',').map((c: string) => c.trim());
        const lat = parseFloat(cols[latIdx]);
        const lon = parseFloat(cols[lonIdx]);
        if (isNaN(lat) || isNaN(lon)) continue;

        const name = nameIdx !== -1 ? cols[nameIdx] : `Point ${i}`;
        const latlng = L.latLng(lat, lon);
        latLngs.push(latlng);

        L.marker(latlng)
          .bindTooltip(name)
          .addTo(this.miniMapLayer as L.LayerGroup);
      }

      if (latLngs.length > 1) {
        L.polyline(latLngs, {
          color: '#1976D2',
          weight: 2,
          dashArray: '6 4',
        }).addTo(this.miniMapLayer as L.LayerGroup);
      }

      setTimeout(() => {
        if (!this.miniMap) return;
        this.miniMap.invalidateSize();
        if (latLngs.length > 1) {
          this.miniMap.fitBounds(L.latLngBounds(latLngs), {
            padding: [20, 20],
          });
        } else if (latLngs.length === 1) {
          this.miniMap.setView(latLngs[0], 14);
        }
      }, 100);
    },

    onDownloadTrackingFile() {
      if (!this.currentTrackingFile || !this.currentTrackingFileName) {
        return;
      }
      const blob = new Blob([this.currentTrackingFile], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', this.currentTrackingFileName);
      link.click();
      URL.revokeObjectURL(url);
    },

    onCreateSubmit() {
      this.createNameError = '';
      this.createDiameterError = '';
      this.createCsvFileError = '';

      const name = this.createName;
      if (!name || name.trim().length === 0) {
        this.createNameError = 'Name is required.';
      } else if (name.trim().length > 500) {
        this.createNameError = 'Name must be 500 characters or less.';
      }

      const diameter = this.createDiameter;
      const MIN_DIAMETER_M = 0.1016;
      const MAX_DIAMETER_M = 1.27;
      let diameterInMeters = 0;
      if (diameter === null || diameter === '') {
        this.createDiameterError = 'Diameter is required.';
      } else if (isNaN(parseFloat(diameter)) || parseFloat(diameter) <= 0) {
        this.createDiameterError = 'Diameter must be a positive number.';
      } else {
        diameterInMeters =
          this.createDiameterUnit === 'in'
            ? parseFloat(diameter) * 0.0254
            : parseFloat(diameter) * 0.01;
        if (
          diameterInMeters < MIN_DIAMETER_M ||
          diameterInMeters > MAX_DIAMETER_M
        ) {
          this.createDiameterError =
            this.createDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.';
        }
      }

      const file = this.createCsvFile;
      if (!file) {
        this.createCsvFileError = 'CSV file is required.';
      } else if (!file.name || file.name.trim().length === 0) {
        this.createCsvFileError = 'File name must not be empty.';
      } else if (file.name.trim().length > 500) {
        this.createCsvFileError = 'File name must be 500 characters or less.';
      } else if (file.size === 0) {
        this.createCsvFileError = 'File must not be empty.';
      }

      if (
        this.createNameError ||
        this.createDiameterError ||
        this.createCsvFileError
      ) {
        return;
      }

      if (this.createCsvValidation && !this.createCsvValidation.valid) {
        return;
      }

      this.loading = true;

      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target?.result as string;
        const validation = this.validateCsv(fileContent);
        this.createCsvValidation = validation;
        if (!validation.valid) {
          this.loading = false;
          return;
        }
        const diameterM =
          this.createDiameterUnit === 'in'
            ? parseFloat(this.createDiameter) * 0.0254
            : parseFloat(this.createDiameter) * 0.01;
        const request = new CreatePipelineRequest(
          this.createName.trim(),
          diameterM,
          this.createCsvFile!.name.trim(),
          fileContent
        );
        PipelineSectionService.createPipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline created.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to create pipeline:', error);
            const msg =
              error?.response?.status === 400 && error?.response?.data
                ? String(error.response.data)
                : 'Failed to create pipeline.';
            this.createNameError = error?.response?.status === 400 ? msg : '';
            if (error?.response?.status !== 400) {
              this.$q.notify({ color: 'negative', message: msg });
            }
          })
          .finally(() => {
            this.loading = false;
          });
      };
      reader.readAsText(this.createCsvFile!);
    },

    onUpdateSubmit() {
      if (!this.selectedPipelineSection || !this.updateDiameter) {
        this.$q.notify({
          color: 'warning',
          message: 'Name and Diameter are required.',
        });
        return;
      }

      if (
        this.updateCsvFile &&
        this.updateCsvValidation &&
        !this.updateCsvValidation.valid
      ) {
        this.$q.notify({
          color: 'warning',
          message: 'Please fix CSV validation errors before updating.',
        });
        return;
      }

      const MIN_DIAMETER_M = 0.1016;
      const MAX_DIAMETER_M = 1.27;
      const updateDiameterM =
        this.updateDiameterUnit === 'in'
          ? parseFloat(this.updateDiameter) * 0.0254
          : parseFloat(this.updateDiameter) * 0.01;
      if (
        isNaN(updateDiameterM) ||
        updateDiameterM < MIN_DIAMETER_M ||
        updateDiameterM > MAX_DIAMETER_M
      ) {
        this.$q.notify({
          color: 'warning',
          message:
            this.updateDiameterUnit === 'in'
              ? 'Diameter must be between 4 and 50 inches.'
              : 'Diameter must be between 10.16 and 127 cm.',
        });
        return;
      }

      this.loading = true;

      const sendUpdate = (
        trackingFileName: string | null,
        trackingFile: string | null
      ) => {
        const diameterM =
          this.updateDiameterUnit === 'in'
            ? parseFloat(this.updateDiameter) * 0.0254
            : parseFloat(this.updateDiameter) * 0.01;
        const request = new UpdatePipelineRequest(
          this.selectedPipelineSection.value,
          diameterM,
          trackingFileName,
          trackingFile
        );
        PipelineSectionService.updatePipelineSection(request)
          .then(() => {
            this.$q.notify({
              color: 'positive',
              message: 'Pipeline updated.',
            });
            this.$emit('update:modelValue', false);
            this.refreshAndEmit();
          })
          .catch((error: any) => {
            console.error('Failed to update pipeline:', error);
            this.$q.notify({
              color: 'negative',
              message: 'Failed to update pipeline.',
            });
          })
          .finally(() => {
            this.loading = false;
          });
      };

      if (this.updateCsvFile) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const fileContent = e.target?.result as string;
          const validation = this.validateCsv(fileContent);
          this.updateCsvValidation = validation;
          if (!validation.valid) {
            this.loading = false;
            return;
          }
          sendUpdate(this.updateCsvFile!.name.trim(), fileContent);
        };
        reader.readAsText(this.updateCsvFile);
      } else {
        sendUpdate(null, null);
      }
    },

    onDeleteSubmit() {
      if (!this.selectedPipelineSection) {
        this.$q.notify({
          color: 'warning',
          message: 'Please select a pipeline section to delete.',
        });
        return;
      }

      this.$q
        .dialog({
          title: 'Confirm Delete',
          message: `Are you sure you want to delete "${this.selectedPipelineSection.label}"? This action cannot be undone.`,
          cancel: true,
          persistent: true,
        })
        .onOk(() => {
          this.loading = true;
          PipelineSectionService.deletePipelineSection(
            this.selectedPipelineSection.value
          )
            .then(() => {
              this.$q.notify({
                color: 'positive',
                message: 'Pipeline deleted.',
              });
              this.selectedPipelineSection = null;
              this.updateDiameter = '';
              this.currentTrackingFileName = '';
              this.currentTrackingFile = '';
              this.refreshAndEmit();
            })
            .catch((error: any) => {
              console.error('Failed to delete pipeline:', error);
              this.$q.notify({
                color: 'negative',
                message: 'Failed to delete pipeline.',
              });
            })
            .finally(() => {
              this.loading = false;
            });
        });
    },

    refreshAndEmit() {
      PipelineSectionService.getPipelineSections()
        .then((response: any) => {
          this.pipelineSections = response.data.map(
            (ps: any) =>
              new PipelineSection(
                ps.pipelineSectionId,
                ps.name,
                ps.diameter,
                ps.trackingFileName,
                ps.trackingFile
              )
          );
          const options = this.pipelineSections
            .map((ps: PipelineSection) => ({
              label: ps.name,
              value: ps.pipelineSectionId,
            }))
            .sort((a: any, b: any) => a.label.localeCompare(b.label));
          this.pipelineSectionOptions = options;
          this.$emit('sections-updated', options);
        })
        .catch((err: any) => {
          console.error('Failed to refresh pipeline sections:', err);
        });
    },
  },
});
</script>

<style scoped>
.manage-ps-dialog .q-tab :deep(.q-tab__label) {
  text-transform: none;
}

.csv-drop-zone {
  border: 2px dashed transparent;
  border-radius: 6px;
  padding: 4px;
  transition: border-color 0.2s, background-color 0.2s;
}

.csv-drop-zone--active {
  border-color: #1976d2;
  background-color: rgba(25, 118, 210, 0.05);
}

.csv-preview-table {
  font-size: 12px;
}

.csv-preview-table :deep(table) {
  table-layout: fixed;
}

.csv-preview-table :deep(td) {
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
}

.csv-preview-table :deep(thead th) {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
}

.csv-preview-table :deep(.q-table__top),
.csv-preview-table :deep(.q-table__bottom) {
  display: none;
}

.csv-preview-table :deep(tr.highlight-flash) {
  animation: flash-row 1.5s ease-out;
}

@keyframes flash-row {
  0% {
    background-color: #ffeb3b;
  }
  100% {
    background-color: transparent;
  }
}
</style>
